// The cart's RBP core marks ("Core", "Core (link)"), read for the SKUs the cart shows that have
// no fresh answer, through `GET /api/v1/rbp/core-marks`, and cached per ordering entry: a product's
// mark depends on the store and the catalogue, not on the line. A change of entry or of signed-in
// user drops the cache; an answer older than `CORE_MARKS_CACHE_TTL_MS` is read again, on the next
// change of the cart or, for a cart left open unchanged, when it expires, since the nightly RBP
// load can move a store's clusters and the core set while a session stays open. An
// unread SKU stays unknown and is asked again.
//
// The read is skipped where the server would answer no marks anyway: a catalogue that shows no RBP
// feature (WOW, Elite, LSN, Corrib Retail), a role outside the browse-side roles, or a licence-trade
// session. The server decides; this only saves the request.

import { useEffect } from 'react';
import { create } from 'zustand';
import { RBP_BROWSE_SIDE_ROLES } from '../auth/rbac';
import { useSessionStore } from '../auth/useSessionStore';
import { fetchCoreMarks, isRbpEntryKind, type RbpCoreMark } from '../api/rbp.client';

type CoreMarkAnswer = RbpCoreMark | 'none';

/** How long an answer is trusted (the on-order chip's figure). */
export const CORE_MARKS_CACHE_TTL_MS = 5 * 60 * 1000;

interface CoreMarksState {
  /** The entry and user the answers belong to: `${userKey}|${entryKey}`. */
  readonly scope: string | null;
  readonly bySku: Readonly<Record<string, CoreMarkAnswer>>;
  load: (scope: string, entryKind: string, skus: readonly string[]) => Promise<void>;
  reset: () => void;
}

const inFlight = new Set<string>();

// When each answer arrived (epoch ms), per scope and SKU, for the TTL.
const answeredAt = new Map<string, number>();

function isFresh(scope: string, sku: string): boolean {
  const at = answeredAt.get(`${scope}|${sku}`);
  return at !== undefined && Date.now() - at < CORE_MARKS_CACHE_TTL_MS;
}

export const useCoreMarksStore = create<CoreMarksState>((set, get) => ({
  scope: null,
  bySku: {},
  async load(scope, entryKind, skus) {
    if (get().scope !== scope) {
      inFlight.clear();
      answeredAt.clear();
      set({ scope, bySku: {} });
    }
    const missing = [...new Set(skus)].filter(
      (sku) => !isFresh(scope, sku) && !inFlight.has(`${scope}|${sku}`),
    );
    if (missing.length === 0) return;
    for (const sku of missing) inFlight.add(`${scope}|${sku}`);
    const { marks, unreadSkus } = await fetchCoreMarks(entryKind, missing);
    for (const sku of missing) inFlight.delete(`${scope}|${sku}`);
    if (get().scope !== scope) return;
    const unread = new Set(unreadSkus);
    const answered: Record<string, CoreMarkAnswer> = {};
    const now = Date.now();
    for (const sku of missing) {
      // An unread SKU keeps its previous answer, if any, and is asked again next time.
      if (unread.has(sku)) continue;
      answered[sku] = marks[sku] ?? 'none';
      answeredAt.set(`${scope}|${sku}`, now);
    }
    set((state) => ({ bySku: { ...state.bySku, ...answered } }));
  },
  reset() {
    inFlight.clear();
    answeredAt.clear();
    set({ scope: null, bySku: {} });
  },
}));

/** The ordering-entry kind of an entry key (`DEPOT:21` is `DEPOT`). */
function entryKindOf(entryKey: string): string {
  return entryKey.split(':')[0] ?? '';
}

/** The core mark of each cart SKU that is core for the caller's store, as far as answered. */
export function useCoreMarks(skus: readonly string[]): Readonly<Record<string, RbpCoreMark>> {
  const userKey = useSessionStore((s) => s.identity?.sub ?? null);
  const entryKey = useSessionStore((s) => s.activeEntryKey);
  const role = useSessionStore((s) => s.role);
  const accountType = useSessionStore((s) => s.accountType);
  const skuKey = [...new Set(skus)].sort().join('\u0000');

  const shows =
    userKey !== null &&
    entryKey !== null &&
    isRbpEntryKind(entryKindOf(entryKey)) &&
    role !== null &&
    RBP_BROWSE_SIDE_ROLES.includes(role) &&
    accountType !== 'LicenceTrade';
  const scope = shows ? `${userKey}|${entryKey}` : null;

  useEffect(() => {
    if (scope === null || entryKey === null || skuKey === '') return;
    const load = () =>
      void useCoreMarksStore.getState().load(scope, entryKindOf(entryKey), skuKey.split('\u0000'));
    load();
    // A cart left open and unchanged reads its marks again once they expire. Answers are stamped when
    // the response lands, after this timer starts, so it ticks at half the TTL: an answer is re-read
    // by one and a half TTLs at most, never left for almost two.
    const timer = setInterval(load, CORE_MARKS_CACHE_TTL_MS / 2);
    return () => clearInterval(timer);
  }, [scope, entryKey, skuKey]);

  const bySku = useCoreMarksStore((s) => (s.scope === scope ? s.bySku : null));
  const marks: Record<string, RbpCoreMark> = {};
  if (bySku !== null) {
    for (const [sku, answer] of Object.entries(bySku)) if (answer !== 'none') marks[sku] = answer;
  }
  return marks;
}
